/**
 * PPC Cabinet — окремий інтерфейс для ролі 'ppc'.
 *
 * Повністю відокремлений від SEO: власний хедер, власний список PPC-проєктів,
 * власний дашборд Google Ads. Рендериться раннім return у App.jsx коли
 * userRole === 'ppc' (за аналогією з finance-роллю).
 *
 * Дані: /api/ppc-projects (CRUD), /api/google-ads/* (OAuth + метрики).
 * Поки немає GOOGLE_ADS_DEVELOPER_TOKEN — підключення акаунта працює, а блок
 * метрик показує стан «очікує developer token».
 */
import { useState, useEffect, useCallback } from "react";
import { Line } from "react-chartjs-2";
import { useApp } from "../context/AppContext";
import ToggleSwitch from "../components/ToggleSwitch";

const CURRENCIES = ["UAH", "USD", "EUR", "PLN"];
const CUR_SIGN = { UAH: "₴", USD: "$", EUR: "€", PLN: "zł" };

// 6 бізнес-вкладок дашборда (під власника / маркетолога / PPC-спеца).
const TABS = [
  { key: "executive", label: "📊 Зведення" },
  { key: "performance", label: "Ефективність" },
  { key: "sales", label: "Продажі" },
  { key: "search", label: "Пошук" },
  { key: "growth", label: "Точки росту" },
  { key: "insights", label: "AI-інсайти" },
];
// Які report-секції тягне кожна вкладка (крім executive/growth/insights — вони на metrics).
const TAB_SECTIONS = {
  performance: ["campaignTypes", "adGroups", "devices", "geo", "dayOfWeek", "hour"],
  sales: ["conversions", "products", "landingPages"],
  search: ["keywords", "searchTerms"],
};
const SECTION_LABEL = {
  keywords: "Ключове слово", searchTerms: "Пошуковий запит", products: "Товар", adGroups: "Група / кампанія",
  devices: "Пристрій", campaignTypes: "Тип реклами", landingPages: "Сторінка", dayOfWeek: "День тижня", hour: "Година", geo: "Регіон",
};
const SECTION_TITLE = {
  keywords: "Ключові слова", searchTerms: "Пошукові запити", products: "Товари", adGroups: "Групи оголошень",
  devices: "Пристрої", campaignTypes: "Типи реклами", landingPages: "Посадкові сторінки", dayOfWeek: "По днях тижня", hour: "По годинах доби", geo: "По регіонах",
};

// Короткі описи кожного показника (для клієнта — що означає цифра).
const METRIC_DESC = {
  cost: "Скільки витрачено на рекламу за період",
  value: "Цінність конверсій (умовний дохід), яку рахує Google Ads",
  roas: "Дохід ÷ Витрати. Скільки повертає кожна вкладена гривня (2× = вдвічі більше, ніж витратили)",
  romi: "(Дохід − Витрати) ÷ Витрати, %. Чистий прибуток на кожну вкладену гривню",
  conversions: "Кількість цільових дій: заявки, покупки, дзвінки",
  cpa: "Середня вартість однієї конверсії (Витрати ÷ Конверсії)",
  cr: "Відсоток кліків, що стали конверсією (Конверсії ÷ Кліки)",
  aov: "Середня цінність однієї конверсії (Дохід ÷ Конверсії)",
  cpc: "Середня ціна одного кліку (Витрати ÷ Кліки)",
  ctr: "Відсоток показів, що дали клік (Кліки ÷ Покази)",
  clicks: "Скільки разів клікнули по оголошеннях",
  impressions: "Скільки разів оголошення показали користувачам",
};
// Описи колонок таблиць (за заголовком).
const COL_DESC = {
  "Витрати": METRIC_DESC.cost, "Дохід": METRIC_DESC.value, "ROAS": METRIC_DESC.roas,
  "Кліки": METRIC_DESC.clicks, "Покази": METRIC_DESC.impressions, "CTR": METRIC_DESC.ctr,
  "CPC": METRIC_DESC.cpc, "Конв.": METRIC_DESC.conversions, "CPA": METRIC_DESC.cpa,
  "Частка": "Частка цього рядка від усіх конверсій", "Цінність": METRIC_DESC.value,
  "Сер. цінність": METRIC_DESC.aov, "Усі конв.": "Усі конверсії, включно з неосновними (не в ставках)",
};
// Підзаголовки вкладок — що показує кожна.
const TAB_DESC = {
  executive: "Головне за 30 секунд: скільки вклали, скільки заробили, чи окупається реклама",
  performance: "Ефективність кампаній і по пристроях",
  sales: "Продажі та конверсії: які дії рахуються і скільки коштують",
  search: "За якими ключами та реальними запитами приходять люди",
  growth: "Що покращити: злитий бюджет, втрачені покази, рекомендації",
  insights: "Автоматичні висновки по акаунту",
};

export default function PpcCabinet() {
  const { t, dark, currentUser, handleLogout } = useApp();
  const [selectedId, setSelectedId] = useState(null);

  return (
    <div style={{ fontFamily: "'Inter', -apple-system, sans-serif", background: t.bg, color: t.text, minHeight: "100vh" }}>
      <link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap" rel="stylesheet" />
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 24px", borderBottom: `1px solid ${t.border}`, position: "sticky", top: 0, zIndex: 100, background: dark ? "rgba(10,10,12,0.85)" : "rgba(245,245,247,0.85)", backdropFilter: "blur(16px)", WebkitBackdropFilter: "blur(16px)" }}>
        <span onClick={() => setSelectedId(null)} style={{ cursor: "pointer", fontWeight: 800, fontSize: 15, letterSpacing: "1px" }}>
          SPALAH <span style={{ background: "linear-gradient(135deg,#22c55e,#16a34a)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>PPC</span>
        </span>
        <span style={{ fontSize: 11, color: t.textMuted, border: `1px solid ${t.border}`, borderRadius: 20, padding: "2px 10px" }}>Рекламний кабінет</span>
        <div style={{ flex: 1 }} />
        {currentUser && <span style={{ fontSize: 12, color: t.textMuted }}>{currentUser}</span>}
        <button onClick={handleLogout} style={{ background: "none", border: `1px solid ${t.border}`, borderRadius: 5, padding: "3px 10px", fontSize: 11, cursor: "pointer", color: t.textMuted, fontFamily: "inherit" }}>Вихід</button>
        <ToggleSwitch />
      </div>

      <div style={{ padding: "24px", maxWidth: 1200, margin: "0 auto" }}>
        {selectedId
          ? <PpcDashboard projectId={selectedId} onBack={() => setSelectedId(null)} />
          : <PpcProjectList onOpen={setSelectedId} />}
      </div>
    </div>
  );
}

// ─────────────────────────── Список проєктів ───────────────────────────
function PpcProjectList({ onOpen }) {
  const { t, dark, apiFetch, card, inp, btnP, btnG, flash } = useApp();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ name: "", domain: "", currency: "UAH" });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await apiFetch("/api/ppc-projects");
      const d = await r.json();
      setProjects(d.projects || []);
    } catch { flash("Помилка завантаження"); }
    setLoading(false);
  }, [apiFetch, flash]);

  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!form.name.trim()) return flash("Вкажіть назву");
    setCreating(true);
    try {
      const r = await apiFetch("/api/ppc-projects", { method: "POST", body: JSON.stringify(form) });
      const d = await r.json();
      if (d.project) { setForm({ name: "", domain: "", currency: "UAH" }); await load(); flash("Проєкт створено"); }
      else flash(d.error || "Помилка");
    } catch { flash("Помилка"); }
    setCreating(false);
  };

  const del = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm("Видалити PPC-проєкт разом з підключенням Google Ads?")) return;
    try { await apiFetch(`/api/ppc-projects/${id}`, { method: "DELETE" }); await load(); }
    catch { flash("Помилка видалення"); }
  };

  return (
    <div>
      <h2 style={{ fontSize: 20, fontWeight: 700, margin: "0 0 4px" }}>PPC-проєкти</h2>
      <p style={{ fontSize: 13, color: t.textMuted, margin: "0 0 20px" }}>Підключайте рекламні акаунти Google Ads і стежте за витратами, кліками, конверсіями та ROAS.</p>

      {/* Create */}
      <div style={{ ...card, display: "flex", gap: 10, alignItems: "flex-end", flexWrap: "wrap" }}>
        <div style={{ flex: "2 1 200px" }}>
          <label style={{ fontSize: 11, color: t.textMuted, display: "block", marginBottom: 4 }}>Назва*</label>
          <input style={inp} value={form.name} placeholder="Напр. Клієнт — Пошук" onChange={e => setForm({ ...form, name: e.target.value })} onKeyDown={e => e.key === "Enter" && create()} />
        </div>
        <div style={{ flex: "2 1 200px" }}>
          <label style={{ fontSize: 11, color: t.textMuted, display: "block", marginBottom: 4 }}>Домен</label>
          <input style={inp} value={form.domain} placeholder="example.com" onChange={e => setForm({ ...form, domain: e.target.value })} onKeyDown={e => e.key === "Enter" && create()} />
        </div>
        <div style={{ flex: "0 1 110px" }}>
          <label style={{ fontSize: 11, color: t.textMuted, display: "block", marginBottom: 4 }}>Валюта</label>
          <select style={inp} value={form.currency} onChange={e => setForm({ ...form, currency: e.target.value })}>
            {CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <button style={{ ...btnP, padding: "9px 18px" }} disabled={creating} onClick={create}>{creating ? "..." : "+ Створити"}</button>
      </div>

      {/* List */}
      {loading ? (
        <div style={{ fontSize: 13, color: t.textMuted, padding: 20 }}>Завантаження...</div>
      ) : projects.length === 0 ? (
        <div style={{ ...card, textAlign: "center", padding: 40, color: t.textMuted, fontSize: 13 }}>Ще немає жодного PPC-проєкту. Створіть перший вище.</div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(280px,1fr))", gap: 12, marginTop: 4 }}>
          {projects.map(p => (
            <div key={p.id} onClick={() => onOpen(p.id)} style={{ ...card, marginBottom: 0, cursor: "pointer", transition: "border-color .15s", position: "relative" }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
                <span style={{ fontSize: 15, fontWeight: 700 }}>{p.name}</span>
                <span style={{ fontSize: 10, padding: "2px 8px", borderRadius: 20, fontWeight: 600, background: p.ads_connected ? "rgba(34,197,94,0.15)" : (dark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.04)"), color: p.ads_connected ? "#22c55e" : t.textMuted }}>
                  {p.ads_connected ? "● Google Ads" : "○ не підключено"}
                </span>
              </div>
              {p.domain && <div style={{ fontSize: 12, color: t.textMuted }}>{p.domain}</div>}
              <div style={{ fontSize: 11, color: t.textMuted, marginTop: 8 }}>Валюта: {p.currency}</div>
              <button onClick={e => del(p.id, e)} title="Видалити" style={{ position: "absolute", top: 10, right: 10, display: "none" }} />
              <div style={{ marginTop: 12, display: "flex", gap: 8 }}>
                <span style={{ ...btnG, fontSize: 11, padding: "4px 10px", pointerEvents: "none" }}>Відкрити дашборд →</span>
                <button onClick={e => del(p.id, e)} style={{ ...btnG, fontSize: 11, padding: "4px 10px", color: "#ef4444", borderColor: "rgba(239,68,68,0.3)" }}>Видалити</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────── Дашборд проєкту ───────────────────────────
function PpcDashboard({ projectId, onBack }) {
  const { t, dark, apiFetch, card, btnP, btnG, flash } = useApp();
  const [project, setProject] = useState(null);
  const [status, setStatus] = useState(null);
  const [days, setDays] = useState(30);
  const [metrics, setMetrics] = useState(null);
  const [loadingM, setLoadingM] = useState(false);
  const [customers, setCustomers] = useState(null);
  const [demo, setDemo] = useState(false);
  const [tab, setTab] = useState("executive");
  const [report, setReport] = useState({});      // { section: {rows,loading,...} }
  const [growth, setGrowth] = useState(null);
  const [shareBusy, setShareBusy] = useState(false);
  const cur = project?.currency || "UAH";

  // Постійне read-only посилання для клієнта на /ppc-report
  const shareLink = async () => {
    setShareBusy(true);
    try {
      const r = await apiFetch(`/api/ppc-projects/${projectId}/share`, { method: "POST" });
      const d = await r.json();
      if (d.token) {
        const url = `${location.origin}/ppc-report?t=${d.token}`;
        try { await navigator.clipboard.writeText(url); flash("Посилання для клієнта скопійовано ✓"); }
        catch { window.prompt("Посилання для клієнта:", url); }
      } else flash(d.error || "Помилка");
    } catch { flash("Помилка"); }
    setShareBusy(false);
  };
  const sign = CUR_SIGN[cur] || "";

  const loadProject = useCallback(async () => {
    try {
      const r = await apiFetch(`/api/ppc-projects/${projectId}`);
      const d = await r.json();
      setProject(d.project);
    } catch { flash("Помилка"); }
  }, [apiFetch, projectId, flash]);

  const loadStatus = useCallback(async () => {
    try {
      const r = await apiFetch(`/api/google-ads/status?ppcProjectId=${projectId}`);
      setStatus(await r.json());
    } catch { setStatus({ connected: false }); }
  }, [apiFetch, projectId]);

  const loadMetrics = useCallback(async () => {
    setLoadingM(true);
    try {
      const r = await apiFetch(`/api/google-ads/metrics?ppcProjectId=${projectId}&days=${days}${demo ? "&demo=1" : ""}`);
      setMetrics(await r.json());
    } catch { setMetrics(null); }
    setLoadingM(false);
  }, [apiFetch, projectId, days, demo]);

  const loadReport = useCallback(async (section) => {
    setReport(prev => ({ ...prev, [section]: { ...(prev[section] || {}), loading: true } }));
    try {
      const r = await apiFetch(`/api/google-ads/report?ppcProjectId=${projectId}&days=${days}&section=${section}${demo ? "&demo=1" : ""}`);
      const d = await r.json();
      setReport(prev => ({ ...prev, [section]: { loading: false, rows: d.rows || [], tokenNotApproved: d.tokenNotApproved, needsCustomer: d.needsCustomer } }));
    } catch {
      setReport(prev => ({ ...prev, [section]: { loading: false, rows: [] } }));
    }
  }, [apiFetch, projectId, days, demo]);

  useEffect(() => { loadProject(); loadStatus(); }, [loadProject, loadStatus]);
  useEffect(() => { if (status?.connected) loadMetrics(); }, [status, days, demo, loadMetrics]);
  // Скидаємо кеш деталізації при зміні періоду/демо; лінива підгрузка при відкритті вкладки.
  const loadGrowth = useCallback(async () => {
    setGrowth({ loading: true });
    try {
      const r = await apiFetch(`/api/google-ads/growth?ppcProjectId=${projectId}&days=${days}${demo ? "&demo=1" : ""}`);
      setGrowth(await r.json());
    } catch { setGrowth({ error: true }); }
  }, [apiFetch, projectId, days, demo]);

  useEffect(() => { setReport({}); setGrowth(null); }, [days, demo]);
  useEffect(() => {
    if (!status?.connected) return;
    (TAB_SECTIONS[tab] || []).forEach(sec => { if (!report[sec]) loadReport(sec); });
    if (tab === "growth" && !growth) loadGrowth();
  }, [tab, status, report, growth, loadReport, loadGrowth]);

  // OAuth popup
  const connect = async () => {
    try {
      const r = await apiFetch(`/api/google-ads/auth-url?ppcProjectId=${projectId}`);
      const d = await r.json();
      if (!d.url) return flash(d.error || "Помилка");
      const w = window.open(d.url, "gads_oauth", "width=520,height=650");
      const onMsg = (ev) => {
        if (ev.data?.type === "GADS_CONNECTED") {
          window.removeEventListener("message", onMsg);
          try { w && w.close(); } catch {}
          flash("Google Ads підключено");
          loadStatus();
        }
      };
      window.addEventListener("message", onMsg);
    } catch { flash("Помилка"); }
  };

  const disconnect = async () => {
    if (!window.confirm("Відключити Google Ads від цього проєкту?")) return;
    try { await apiFetch(`/api/google-ads/disconnect?ppcProjectId=${projectId}`, { method: "DELETE" }); setMetrics(null); setCustomers(null); loadStatus(); }
    catch { flash("Помилка"); }
  };

  const loadCustomers = async () => {
    setCustomers("loading");
    try {
      const r = await apiFetch(`/api/google-ads/customers?ppcProjectId=${projectId}`);
      const d = await r.json();
      setCustomers(d.devTokenMissing ? "devTokenMissing" : d.tokenNotApproved ? "tokenNotApproved" : (d.customers || []));
    } catch { setCustomers([]); }
  };

  const selectCustomer = async (cid) => {
    try {
      await apiFetch("/api/google-ads/select-customer", { method: "POST", body: JSON.stringify({ ppcProjectId: projectId, customerId: cid }) });
      flash("Акаунт обрано"); setCustomers(null); loadStatus(); loadMetrics();
    } catch { flash("Помилка"); }
  };

  const S = metrics?.summary, P = metrics?.prevSummary;
  const dl = (cur, prev, higherBetter = true) => {
    if (prev == null || prev === 0) return null;
    const p = Math.round((cur - prev) / prev * 100);
    return { p, good: higherBetter ? p >= 0 : p <= 0 };
  };
  const kpis = S ? [
    { label: "Витрати", value: `${sign}${fmt(S.cost)}`, color: "#ef4444", d: dl(S.cost, P?.cost, false), info: METRIC_DESC.cost },
    { label: "Дохід (усі конв.)", value: `${sign}${fmt(S.value)}`, color: "#16a34a", d: dl(S.value, P?.value, true), info: METRIC_DESC.value },
    ...(S.salesValue != null ? [{ label: "Дохід · продажі", value: `${sign}${fmt(S.salesValue)}`, color: "#15803d", d: dl(S.salesValue, P?.salesValue, true), info: "Цінність ЛИШЕ прямих покупок (без дзвінків і маршрутів). Чистий дохід для ROAS." }] : []),
    { label: "ROAS", value: `${S.roas}×`, color: "#16a34a", d: dl(S.roas, P?.roas, true), info: METRIC_DESC.roas },
    ...(S.salesValue != null ? [{ label: "ROAS · продажі", value: `${S.cost ? +(S.salesValue / S.cost).toFixed(2) : 0}×`, color: "#15803d", d: dl(S.cost ? S.salesValue / S.cost : 0, P?.cost ? P.salesValue / P.cost : 0, true), info: "Окупність лише за прямими продажами (дохід·продажі ÷ витрати)." }] : []),
    { label: "ROMI", value: `${S.romi}%`, color: "#10b981", d: dl(S.romi, P?.romi, true), info: METRIC_DESC.romi },
    { label: "Конверсії", value: fmt(S.conversions), color: "#22c55e", d: dl(S.conversions, P?.conversions, true), info: METRIC_DESC.conversions },
    { label: "CPA", value: `${sign}${fmt(S.cpa)}`, color: "#f97316", d: dl(S.cpa, P?.cpa, false), info: METRIC_DESC.cpa },
    { label: "CR", value: `${S.cr}%`, color: "#06b6d4", d: dl(S.cr, P?.cr, true), info: METRIC_DESC.cr },
    { label: "Сер. чек", value: `${sign}${fmt(S.aov)}`, color: "#a855f7", d: dl(S.aov, P?.aov, true), info: METRIC_DESC.aov },
    { label: "CPC", value: `${sign}${fmt(S.cpc)}`, color: "#f59e0b", d: dl(S.cpc, P?.cpc, false), info: METRIC_DESC.cpc },
    { label: "CTR", value: `${S.ctr}%`, color: "#06b6d4", d: dl(S.ctr, P?.ctr, true), info: METRIC_DESC.ctr },
    { label: "Кліки", value: fmt(S.clicks), color: "#3b82f6", d: dl(S.clicks, P?.clicks, true), info: METRIC_DESC.clicks },
    { label: "Покази", value: fmt(S.impressions), color: "#8b5cf6", d: dl(S.impressions, P?.impressions, true), info: METRIC_DESC.impressions },
  ] : [];

  return (
    <div>
      <button onClick={onBack} style={{ ...btnG, marginBottom: 14 }}>← До списку</button>
      {project && (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 10, marginBottom: 16 }}>
          <div>
            <h2 style={{ fontSize: 20, fontWeight: 700, margin: 0 }}>{project.name}</h2>
            {project.domain && <div style={{ fontSize: 13, color: t.textMuted }}>{project.domain}</div>}
            <button onClick={shareLink} disabled={shareBusy} style={{ ...btnG, fontSize: 11, marginTop: 8, background: "linear-gradient(135deg,#6366f1,#a855f7)", color: "#fff", border: "none", padding: "6px 14px", cursor: "pointer", opacity: shareBusy ? 0.6 : 1 }} title="Скопіювати постійне read-only посилання на дашборд для клієнта">🔗 {shareBusy ? "..." : "Посилання для клієнта"}</button>
          </div>
          {status?.connected ? (
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ fontSize: 12, color: "#22c55e" }}>● {status.email || "Google Ads підключено"}</span>
              <button onClick={() => setDemo(v => !v)} style={{ ...btnG, fontSize: 11, ...(demo ? { background: "#f59e0b", color: "#fff", borderColor: "#f59e0b" } : {}) }} title="Прев'ю дашборда на прикладних даних">👁 Демо-дані</button>
              <button onClick={disconnect} style={{ ...btnG, fontSize: 11, color: "#ef4444", borderColor: "rgba(239,68,68,0.3)" }}>Відключити</button>
            </div>
          ) : (
            <button onClick={connect} style={{ ...btnP, background: "linear-gradient(135deg,#22c55e,#16a34a)", padding: "9px 18px" }}>Підключити Google Ads</button>
          )}
        </div>
      )}

      {!status?.connected && (
        <div style={{ ...card, textAlign: "center", padding: 40 }}>
          <div style={{ fontSize: 40, marginBottom: 10 }}>📊</div>
          <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 6 }}>Підключіть рекламний акаунт Google Ads</div>
          <div style={{ fontSize: 13, color: t.textMuted, maxWidth: 460, margin: "0 auto 16px" }}>
            Авторизуйтесь через Google, щоб бачити витрати, кліки, конверсії та ROAS по кампаніях у єдиному дашборді.
          </div>
          <button onClick={connect} style={{ ...btnP, background: "linear-gradient(135deg,#22c55e,#16a34a)", padding: "10px 22px" }}>Підключити Google Ads</button>
        </div>
      )}

      {status?.connected && status?.devTokenMissing && (
        <div style={{ ...card, borderColor: "rgba(245,158,11,0.4)", background: dark ? "rgba(245,158,11,0.08)" : "rgba(245,158,11,0.06)" }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: "#f59e0b", marginBottom: 4 }}>⏳ Очікує Google Ads developer token</div>
          <div style={{ fontSize: 13, color: t.textMuted, lineHeight: 1.5 }}>
            Акаунт успішно підключено. Дані кампаній зʼявляться після того, як у систему додадуть <b>developer token</b> Google Ads API
            (видається в MCC-акаунті → API Center). Підключення й токени вже збережені — щойно токен буде додано, дашборд запрацює автоматично.
          </div>
        </div>
      )}

      {/* Вибір акаунта (коли токен є, але акаунт ще не обрано) */}
      {status?.connected && !status?.devTokenMissing && !status?.customerId && !demo && (
        <div style={{ ...card }}>
          <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 8 }}>Оберіть рекламний акаунт</div>
          {customers == null && <button onClick={loadCustomers} style={btnP}>Завантажити акаунти</button>}
          {customers === "loading" && <div style={{ fontSize: 13, color: t.textMuted }}>Завантаження...</div>}
          {customers === "devTokenMissing" && <div style={{ fontSize: 13, color: "#f59e0b" }}>Потрібен developer token.</div>}
          {customers === "tokenNotApproved" && (
            <div style={{ fontSize: 13, color: "#f59e0b", lineHeight: 1.5 }}>
              ⏳ Developer token поки на рівні <b>Test access</b> — Google ще не апрував доступ до реальних акаунтів.
              Заявку на <b>Basic access</b> подано; зазвичай апрув за 1–2 дні. Щойно схвалять — натисни «Завантажити акаунти» знову.
            </div>
          )}
          {Array.isArray(customers) && customers.length === 0 && <div style={{ fontSize: 13, color: t.textMuted }}>Немає доступних акаунтів.</div>}
          {Array.isArray(customers) && customers.length > 0 && (
            <div style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: 8 }}>
              {customers.map(c => (
                <div key={c.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 12px", border: `1px solid ${t.border}`, borderRadius: 8 }}>
                  <span style={{ fontSize: 13 }}>{c.name} <span style={{ color: t.textMuted, fontSize: 11 }}>({c.id}{c.manager ? " · MCC" : ""}{c.currency ? " · " + c.currency : ""})</span></span>
                  {!c.manager && <button onClick={() => selectCustomer(c.id)} style={{ ...btnP, fontSize: 11, padding: "4px 12px" }}>Обрати</button>}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Метрики */}
      {status?.connected && (demo || (!status?.devTokenMissing && status?.customerId)) && (
        <>
          {demo && (
            <div style={{ marginBottom: 12, padding: "8px 14px", borderRadius: 8, background: dark ? "rgba(245,158,11,0.1)" : "rgba(245,158,11,0.08)", border: "1px solid rgba(245,158,11,0.35)", fontSize: 12, color: "#f59e0b", fontWeight: 600 }}>
              👁 Демо-режим — прикладні дані для прев'ю. Реальні дані підтягнуться автоматично після апруву Basic access.
            </div>
          )}
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
            <span style={{ fontSize: 12, color: t.textMuted }}>Період:</span>
            {[7, 14, 30, 90].map(d => (
              <button key={d} onClick={() => setDays(d)} style={{ ...btnG, fontSize: 11, padding: "4px 10px", ...(days === d ? { background: t.accent, color: "#fff", borderColor: t.accent } : {}) }}>{d} дн</button>
            ))}
            <div style={{ flex: 1 }} />
            <button onClick={() => { loadMetrics(); (TAB_SECTIONS[tab] || []).forEach(loadReport); if (tab === "growth") loadGrowth(); }} style={{ ...btnG, fontSize: 11 }}>⟳ Оновити</button>
          </div>

          {/* Таб-бар деталізації */}
          <div style={{ display: "flex", gap: 6, marginBottom: 14, flexWrap: "wrap", borderBottom: `1px solid ${t.border}`, paddingBottom: 8 }}>
            {TABS.map(tb => (
              <button key={tb.key} onClick={() => setTab(tb.key)} title={TAB_DESC[tb.key]} style={{ ...btnG, fontSize: 12, padding: "5px 12px", ...(tab === tb.key ? { background: t.accent, color: "#fff", borderColor: t.accent } : {}) }}>{tb.label}</button>
            ))}
          </div>
          {TAB_DESC[tab] && <div style={{ fontSize: 12, color: t.textMuted, marginBottom: 14, marginTop: -4 }}>{TAB_DESC[tab]}</div>}

          {loadingM && <div style={{ fontSize: 13, color: t.textMuted, padding: 12 }}>Завантаження...</div>}

          {metrics?.tokenNotApproved && (
            <div style={{ ...card, borderColor: "rgba(245,158,11,0.4)", background: dark ? "rgba(245,158,11,0.08)" : "rgba(245,158,11,0.06)" }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: "#f59e0b", marginBottom: 4 }}>⏳ Developer token на рівні Test access</div>
              <div style={{ fontSize: 13, color: t.textMuted, lineHeight: 1.5 }}>Google ще не апрував доступ до реальних акаунтів. Заявку на Basic access подано — після схвалення дані підтягнуться автоматично. Увімкни «Демо-дані», щоб побачити приклад.</div>
            </div>
          )}

          {/* ══ ЗВЕДЕННЯ (Executive) ══ */}
          {tab === "executive" && metrics?.summary && (
            <>
              {!demo && metrics.summary.roas > 0 && metrics.summary.roas < 1 && (
                <div style={{ marginBottom: 14, padding: "10px 14px", borderRadius: 8, background: dark ? "rgba(245,158,11,0.1)" : "rgba(245,158,11,0.08)", border: "1px solid rgba(245,158,11,0.35)", fontSize: 12.5, color: t.text, lineHeight: 1.5 }}>
                  <b style={{ color: "#f59e0b" }}>⚠️ ROAS {metrics.summary.roas}× — цінність конверсій нижча за витрати.</b> Ймовірно, у конверсії не передається реальна вартість продажу. Перевір розбивку у вкладці <b>Продажі → Конверсії за типами</b>.
                </div>
              )}
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(140px,1fr))", gap: 10, marginBottom: 16 }}>
                {kpis.map(k => (
                  <div key={k.label} title={k.info} style={{ ...card, marginBottom: 0, padding: 14 }}>
                    <div style={{ fontSize: 11, color: t.textMuted, marginBottom: 4, display: "flex", alignItems: "center", gap: 4 }}>
                      {k.label}
                      <span title={k.info} style={{ cursor: "help", fontSize: 9, width: 13, height: 13, borderRadius: "50%", border: `1px solid ${t.textMuted}`, color: t.textMuted, display: "inline-flex", alignItems: "center", justifyContent: "center", opacity: 0.6 }}>?</span>
                    </div>
                    <div style={{ fontSize: 20, fontWeight: 800, color: k.color }}>{k.value}</div>
                    {k.d && (
                      <div style={{ fontSize: 11, fontWeight: 700, marginTop: 3, color: k.d.good ? "#16a34a" : "#ef4444" }}>
                        {k.d.p > 0 ? "▲ +" : k.d.p < 0 ? "▼ " : ""}{k.d.p}% <span style={{ color: t.textMuted, fontWeight: 400 }}>vs попер.</span>
                      </div>
                    )}
                    <div style={{ fontSize: 10, color: t.textMuted, marginTop: 6, lineHeight: 1.35 }}>{k.info}</div>
                  </div>
                ))}
              </div>

              {metrics.salesBreakdown?.current && (() => {
                const b = metrics.salesBreakdown.current;
                const chips = [
                  { l: "🛒 Покупки", n: b.purchase.conversions, sub: b.purchase.value ? `${sign}${fmt(b.purchase.value)}` : "кінцева покупка", c: "#16a34a" },
                  { l: "📞 Дзвінки", n: b.phone.conversions, sub: "клік по телефону", c: "#0ea5e9" },
                  { l: "🧭 Маршрути", n: b.directions.conversions, sub: "перегляд маршруту", c: "#a855f7" },
                ];
                if (b.other?.conversions > 0) chips.push({ l: "✦ Інші дії", n: b.other.conversions, sub: "заявки, форми", c: "#f59e0b" });
                return (
                  <div style={{ ...card, marginBottom: 16 }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: t.textMuted, textTransform: "uppercase", letterSpacing: 1, marginBottom: 12 }}>Розподіл конверсій за типом</div>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(150px,1fr))", gap: 12 }}>
                      {chips.map(ch => (
                        <div key={ch.l} style={{ border: `1px solid ${t.border}`, borderTop: `3px solid ${ch.c}`, borderRadius: 12, padding: "12px 14px" }}>
                          <div style={{ fontSize: 12.5, fontWeight: 700 }}>{ch.l}</div>
                          <div style={{ fontSize: 24, fontWeight: 800, color: ch.c, marginTop: 6 }}>{Math.round(ch.n).toLocaleString("uk-UA")}</div>
                          <div style={{ fontSize: 10.5, color: t.textMuted, marginTop: 3 }}>{ch.sub}</div>
                        </div>
                      ))}
                    </div>
                    <div style={{ fontSize: 11, color: t.textMuted, marginTop: 10, lineHeight: 1.4 }}>«Дохід · продажі» і «ROAS · продажі» рахуються лише з покупок. Дзвінки та маршрути — мікроконверсії, вони не додають прямого доходу.</div>
                  </div>
                );
              })()}

              {metrics.insights?.length > 0 && <InsightsBlock insights={metrics.insights} t={t} card={card} dark={dark} />}

              {metrics?.daily?.length > 0 && (
                <div style={{ ...card }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: t.textMuted, textTransform: "uppercase", letterSpacing: 1, marginBottom: 10 }}>Динаміка: витрати / дохід / кліки</div>
                  <div style={{ height: 260 }}>
                    <Line
                      data={{
                        labels: metrics.daily.map(d => d.date.slice(5)),
                        datasets: [
                          { label: `Витрати (${sign})`, data: metrics.daily.map(d => +d.cost.toFixed(2)), borderColor: "#ef4444", backgroundColor: "rgba(239,68,68,0.1)", yAxisID: "y", tension: 0.3, fill: true },
                          { label: `Дохід (${sign})`, data: metrics.daily.map(d => +(d.value || 0).toFixed(2)), borderColor: "#16a34a", backgroundColor: "rgba(22,163,74,0.08)", yAxisID: "y", tension: 0.3, fill: true },
                          { label: "Кліки", data: metrics.daily.map(d => d.clicks), borderColor: "#3b82f6", yAxisID: "y1", tension: 0.3 },
                        ],
                      }}
                      options={{
                        responsive: true, maintainAspectRatio: false,
                        interaction: { mode: "index", intersect: false },
                        scales: {
                          y: { position: "left", grid: { color: dark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.05)" }, ticks: { color: t.textMuted } },
                          y1: { position: "right", grid: { drawOnChartArea: false }, ticks: { color: t.textMuted } },
                          x: { grid: { display: false }, ticks: { color: t.textMuted, maxTicksLimit: 12 } },
                        },
                        plugins: { legend: { labels: { color: t.text, boxWidth: 12, font: { size: 11 } } } },
                      }}
                    />
                  </div>
                </div>
              )}
            </>
          )}

          {/* ══ ЕФЕКТИВНІСТЬ (Performance) ══ */}
          {tab === "performance" && (
            <>
              {metrics?.campaigns?.length > 0 && <CampaignsTable campaigns={metrics.campaigns} sign={sign} t={t} card={card} />}
              {["campaignTypes", "adGroups", "devices", "geo", "dayOfWeek", "hour"].map(sec => (
                <ReportTable key={sec} state={report[sec]} labelCol={SECTION_LABEL[sec]} title={SECTION_TITLE[sec]} sign={sign} t={t} card={card} dark={dark} />
              ))}
            </>
          )}

          {/* ══ ПРОДАЖІ (Sales) ══ */}
          {tab === "sales" && (
            <>
              <ConversionsTable state={report.conversions} sign={sign} t={t} card={card} dark={dark} />
              <ReportTable state={report.products} labelCol={SECTION_LABEL.products} title={SECTION_TITLE.products} sign={sign} t={t} card={card} dark={dark} />
              <ReportTable state={report.landingPages} labelCol={SECTION_LABEL.landingPages} title={SECTION_TITLE.landingPages} sign={sign} t={t} card={card} dark={dark} />
            </>
          )}

          {/* ══ ПОШУК (Search) ══ */}
          {tab === "search" && (
            <>
              <ReportTable state={report.keywords} labelCol={SECTION_LABEL.keywords} title={SECTION_TITLE.keywords} sign={sign} t={t} card={card} dark={dark} />
              <ReportTable state={report.searchTerms} labelCol={SECTION_LABEL.searchTerms} title={SECTION_TITLE.searchTerms} sign={sign} t={t} card={card} dark={dark} />
            </>
          )}

          {/* ══ ТОЧКИ РОСТУ (Growth) ══ */}
          {tab === "growth" && (
            <GrowthTab growth={growth} sign={sign} t={t} card={card} dark={dark} />
          )}

          {/* ══ AI-ІНСАЙТИ (Insights) ══ */}
          {tab === "insights" && (
            <>
              {metrics?.insights?.length > 0
                ? <InsightsBlock insights={metrics.insights} t={t} card={card} dark={dark} />
                : <div style={{ ...card, textAlign: "center", color: t.textMuted, fontSize: 13, padding: 24 }}>Інсайти зʼявляться, коли будуть дані.</div>}
              <div style={{ ...card, fontSize: 12, color: t.textMuted, lineHeight: 1.6 }}>
                🔜 Скоро: AI-сводка для директора (через Claude), історія змін в акаунті та порівняння періодів текстом.
              </div>
            </>
          )}

          {tab === "executive" && metrics && !loadingM && !metrics.summary && !metrics.tokenNotApproved && (
            <div style={{ ...card, textAlign: "center", color: t.textMuted, fontSize: 13, padding: 30 }}>Немає даних за обраний період.</div>
          )}
        </>
      )}
    </div>
  );
}

// Блок авто-інсайтів (світлофор).
function InsightsBlock({ insights, t, card, dark, title }) {
  const COLOR = { green: "#16a34a", yellow: "#f59e0b", red: "#ef4444" };
  const ICON = { green: "🟢", yellow: "🟡", red: "🔴" };
  return (
    <div style={{ ...card }}>
      <div style={{ fontSize: 12, fontWeight: 600, color: t.textMuted, textTransform: "uppercase", letterSpacing: 1, marginBottom: 10 }}>{title || "Авто-інсайти"}</div>
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        {insights.map((ins, i) => (
          <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 12px", borderRadius: 8, background: dark ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.02)", borderLeft: `3px solid ${COLOR[ins.level] || t.border}` }}>
            <span style={{ fontSize: 12 }}>{ICON[ins.level] || "•"}</span>
            <span style={{ fontSize: 13, color: t.text }}>{ins.text}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// Таблиця кампаній (повний набір колонок).
function CampaignsTable({ campaigns, sign, t, card }) {
  return (
    <div style={{ ...card }}>
      <div style={{ fontSize: 12, fontWeight: 600, color: t.textMuted, textTransform: "uppercase", letterSpacing: 1, marginBottom: 10 }}>Кампанії</div>
      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
          <thead>
            <tr style={{ color: t.textMuted, fontSize: 11, textTransform: "uppercase" }}>
              <th style={{ textAlign: "left", padding: "6px 8px" }}>Кампанія</th>
              {["Витрати", "Дохід", "ROAS", "Кліки", "Покази", "CTR", "CPC", "Конв.", "CPA"].map(h =>
                <th key={h} title={COL_DESC[h]} style={{ textAlign: "right", padding: "6px 8px", whiteSpace: "nowrap", cursor: COL_DESC[h] ? "help" : "default" }}>{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {campaigns.map(c => {
              const ctr = c.impressions ? (c.clicks / c.impressions * 100).toFixed(2) : 0;
              const cpc = c.clicks ? (c.cost / c.clicks) : 0;
              const cpa = c.conversions ? (c.cost / c.conversions) : 0;
              const roas = c.cost ? (c.value / c.cost).toFixed(2) : 0;
              return (
                <tr key={c.id} style={{ borderTop: `1px solid ${t.border}` }}>
                  <td style={{ padding: "7px 8px" }}>{c.name}</td>
                  <td style={{ padding: "7px 8px", textAlign: "right", whiteSpace: "nowrap" }}>{sign}{fmt(c.cost)}</td>
                  <td style={{ padding: "7px 8px", textAlign: "right", whiteSpace: "nowrap" }}>{sign}{fmt(+(c.value || 0).toFixed(0))}</td>
                  <td style={{ padding: "7px 8px", textAlign: "right", fontWeight: 700, color: roas >= 4 ? "#16a34a" : roas >= 2 ? "#f59e0b" : "#ef4444" }}>{roas}×</td>
                  <td style={{ padding: "7px 8px", textAlign: "right" }}>{fmt(c.clicks)}</td>
                  <td style={{ padding: "7px 8px", textAlign: "right" }}>{fmt(c.impressions)}</td>
                  <td style={{ padding: "7px 8px", textAlign: "right" }}>{ctr}%</td>
                  <td style={{ padding: "7px 8px", textAlign: "right", whiteSpace: "nowrap" }}>{sign}{fmt(+cpc.toFixed(2))}</td>
                  <td style={{ padding: "7px 8px", textAlign: "right" }}>{fmt(+c.conversions.toFixed(1))}</td>
                  <td style={{ padding: "7px 8px", textAlign: "right", whiteSpace: "nowrap" }}>{cpa ? sign + fmt(+cpa.toFixed(2)) : "—"}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// Вкладка «Точки росту»: злитий бюджет + втрачені покази + рекомендації Google.
function GrowthTab({ growth, sign, t, card, dark }) {
  if (!growth || growth.loading) return <div style={{ fontSize: 13, color: t.textMuted, padding: 16 }}>Аналіз точок росту…</div>;
  if (growth.tokenNotApproved) return <div style={{ ...card, fontSize: 13, color: "#f59e0b" }}>Реальні дані — після апруву Basic access. Увімкни «Демо-дані».</div>;
  if (growth.needsCustomer) return <div style={{ ...card, fontSize: 13, color: t.textMuted }}>Спершу оберіть рекламний акаунт.</div>;
  if (growth.error) return <div style={{ ...card, fontSize: 13, color: "#ef4444" }}>Не вдалося завантажити.</div>;
  const wasted = growth.wasted || [], is = growth.impressionShare || [], recs = growth.recommendations || [];
  return (
    <>
      {/* Злитий бюджет */}
      <div style={{ ...card }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: t.textMuted, textTransform: "uppercase", letterSpacing: 1, marginBottom: 6 }}>🔴 Злитий бюджет</div>
        <div style={{ fontSize: 11, color: t.textMuted, marginBottom: 10 }}>Пошукові запити, за якими були витрати, але 0 конверсій — кандидати в мінус-слова</div>
        {wasted.length === 0 ? <div style={{ fontSize: 13, color: t.textMuted, padding: 8 }}>Немає помітно злитих запитів 👍</div> : (
          <>
            <div style={{ fontSize: 20, fontWeight: 800, color: "#ef4444", marginBottom: 10 }}>{sign}{fmt(growth.wastedTotal)} <span style={{ fontSize: 12, fontWeight: 400, color: t.textMuted }}>витрачено без конверсій ({wasted.length} запитів)</span></div>
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                <thead><tr style={{ color: t.textMuted, fontSize: 11, textTransform: "uppercase" }}>
                  <th style={{ textAlign: "left", padding: "6px 8px" }}>Запит</th>
                  <th style={{ textAlign: "right", padding: "6px 8px" }}>Витрати</th>
                  <th style={{ textAlign: "right", padding: "6px 8px" }}>Кліки</th>
                </tr></thead>
                <tbody>
                  {wasted.map((x, i) => (
                    <tr key={i} style={{ borderTop: `1px solid ${t.border}` }}>
                      <td style={{ padding: "7px 8px" }}>{x.name}{x.sub && <div style={{ fontSize: 11, color: t.textMuted }}>{x.sub}</div>}</td>
                      <td style={{ padding: "7px 8px", textAlign: "right", whiteSpace: "nowrap", color: "#ef4444", fontWeight: 600 }}>{sign}{fmt(x.cost)}</td>
                      <td style={{ padding: "7px 8px", textAlign: "right" }}>{fmt(x.clicks)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      {/* Втрачені покази */}
      <div style={{ ...card }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: t.textMuted, textTransform: "uppercase", letterSpacing: 1, marginBottom: 6 }}>📉 Втрачені покази (Impression Share)</div>
        <div style={{ fontSize: 11, color: t.textMuted, marginBottom: 10 }}>Скільки % показів недоотримано через малий бюджет або низький ранг (ставка/якість)</div>
        {is.length === 0 ? <div style={{ fontSize: 13, color: t.textMuted, padding: 8 }}>Немає даних Impression Share (лише для Пошуку/Shopping).</div> : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead><tr style={{ color: t.textMuted, fontSize: 11, textTransform: "uppercase" }}>
                <th style={{ textAlign: "left", padding: "6px 8px" }}>Кампанія</th>
                <th title="Отримана частка показів" style={{ textAlign: "right", padding: "6px 8px", cursor: "help" }}>IS</th>
                <th title="Втрачено показів через нестачу бюджету" style={{ textAlign: "right", padding: "6px 8px", cursor: "help" }}>Втрата: бюджет</th>
                <th title="Втрачено показів через низький ранг (ставка/якість)" style={{ textAlign: "right", padding: "6px 8px", cursor: "help" }}>Втрата: ранг</th>
              </tr></thead>
              <tbody>
                {is.map((x, i) => (
                  <tr key={i} style={{ borderTop: `1px solid ${t.border}` }}>
                    <td style={{ padding: "7px 8px" }}>{x.name}</td>
                    <td style={{ padding: "7px 8px", textAlign: "right" }}>{x.is != null ? x.is + "%" : "—"}</td>
                    <td style={{ padding: "7px 8px", textAlign: "right", color: (x.budgetLost || 0) >= 15 ? "#ef4444" : t.text, fontWeight: (x.budgetLost || 0) >= 15 ? 700 : 400 }}>{x.budgetLost != null ? x.budgetLost + "%" : "—"}</td>
                    <td style={{ padding: "7px 8px", textAlign: "right", color: (x.rankLost || 0) >= 15 ? "#f59e0b" : t.text }}>{x.rankLost != null ? x.rankLost + "%" : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Рекомендації Google */}
      <div style={{ ...card }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: t.textMuted, textTransform: "uppercase", letterSpacing: 1, marginBottom: 6 }}>💡 Рекомендації Google Ads</div>
        <div style={{ fontSize: 11, color: t.textMuted, marginBottom: 10 }}>Що радить сам Google для покращення акаунта (з Recommendations API)</div>
        {recs.length === 0 ? <div style={{ fontSize: 13, color: t.textMuted, padding: 8 }}>Активних рекомендацій немає.</div> : (
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {recs.map((r, i) => (
              <div key={i} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 12px", borderRadius: 8, background: dark ? "rgba(99,102,241,0.06)" : "rgba(99,102,241,0.04)", border: `1px solid ${t.border}` }}>
                <span style={{ fontSize: 13 }}>{r.label}</span>
                {r.count > 1 && <span style={{ fontSize: 11, color: t.textMuted }}>×{r.count}</span>}
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
}

// Розріз по типах конверсій (діагностика цінності/ROAS).
function ConversionsTable({ state, sign, t, card, dark }) {
  if (!state || state.loading) return <div style={{ fontSize: 13, color: t.textMuted, padding: 16 }}>Конверсії за типами: завантаження…</div>;
  if (state.tokenNotApproved) return (
    <div style={{ ...card, borderColor: "rgba(245,158,11,0.4)", background: dark ? "rgba(245,158,11,0.08)" : "rgba(245,158,11,0.06)" }}>
      <div style={{ fontSize: 13, color: "#f59e0b" }}>Реальні дані зʼявляться після апруву Basic access. Увімкни «Демо-дані» для прикладу.</div>
    </div>
  );
  const rows = state.rows || [];
  if (!rows.length) return <div style={{ ...card, textAlign: "center", color: t.textMuted, fontSize: 13, padding: 24 }}>Немає даних по конверсіях за період.</div>;
  const totalConv = rows.reduce((a, r) => a + r.conversions, 0);
  const noValue = rows.filter(r => r.conversions >= 1 && (!r.value || r.value < 1));
  return (
    <div style={{ ...card }}>
      <div style={{ fontSize: 12, fontWeight: 600, color: t.textMuted, textTransform: "uppercase", letterSpacing: 1, marginBottom: 6 }}>Конверсії за типами</div>
      <div style={{ fontSize: 11, color: t.textMuted, marginBottom: 10 }}>Показує, які дії рахуються як конверсії і чи передається їхня вартість (діагностика ROAS)</div>
      {noValue.length > 0 && (
        <div style={{ marginBottom: 10, padding: "8px 12px", borderRadius: 8, background: dark ? "rgba(239,68,68,0.08)" : "rgba(239,68,68,0.05)", border: "1px solid rgba(239,68,68,0.3)", fontSize: 12, color: t.text }}>
          🔴 Без вартості: <b>{noValue.map(r => r.name).join(", ")}</b> — конверсії є, а цінність = 0. Через це ROAS занижений.
        </div>
      )}
      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
          <thead>
            <tr style={{ color: t.textMuted, fontSize: 11, textTransform: "uppercase" }}>
              <th style={{ textAlign: "left", padding: "6px 8px" }}>Тип конверсії</th>
              {["Конверсії", "Частка", "Цінність", "Сер. цінність", "Усі конв."].map(h =>
                <th key={h} title={COL_DESC[h]} style={{ textAlign: "right", padding: "6px 8px", whiteSpace: "nowrap", cursor: COL_DESC[h] ? "help" : "default" }}>{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i} style={{ borderTop: `1px solid ${t.border}` }}>
                <td style={{ padding: "7px 8px" }}>{r.name}</td>
                <td style={{ padding: "7px 8px", textAlign: "right" }}>{fmt(+r.conversions.toFixed(1))}</td>
                <td style={{ padding: "7px 8px", textAlign: "right" }}>{totalConv ? Math.round(r.conversions / totalConv * 100) : 0}%</td>
                <td style={{ padding: "7px 8px", textAlign: "right", whiteSpace: "nowrap", color: (r.conversions >= 1 && r.value < 1) ? "#ef4444" : t.text }}>{r.value ? sign + fmt(+r.value.toFixed(0)) : "—"}</td>
                <td style={{ padding: "7px 8px", textAlign: "right", whiteSpace: "nowrap" }}>{r.avgValue ? sign + fmt(r.avgValue) : "—"}</td>
                <td style={{ padding: "7px 8px", textAlign: "right", color: t.textMuted }}>{fmt(+r.allConversions.toFixed(1))}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// Універсальна таблиця деталізації (ключові слова / запити / товари / групи / пристрої).
function ReportTable({ state, labelCol, title, sign, t, card, dark }) {
  if (!state || state.loading) return <div style={{ fontSize: 13, color: t.textMuted, padding: 16 }}>{title ? title + ": " : ""}завантаження…</div>;
  if (state.tokenNotApproved) return (
    <div style={{ ...card, borderColor: "rgba(245,158,11,0.4)", background: dark ? "rgba(245,158,11,0.08)" : "rgba(245,158,11,0.06)" }}>
      <div style={{ fontSize: 14, fontWeight: 700, color: "#f59e0b", marginBottom: 4 }}>⏳ Developer token на рівні Test access</div>
      <div style={{ fontSize: 13, color: t.textMuted }}>Реальні дані зʼявляться після апруву Basic access. Увімкни «Демо-дані», щоб побачити приклад.</div>
    </div>
  );
  if (state.needsCustomer) return <div style={{ fontSize: 13, color: t.textMuted, padding: 16 }}>Спершу оберіть рекламний акаунт.</div>;
  const rows = state.rows || [];
  if (!rows.length) return <div style={{ ...card, textAlign: "center", color: t.textMuted, fontSize: 13, padding: 30 }}>Немає даних за обраний період.</div>;

  const cols = [
    { h: "Витрати", r: x => `${sign}${fmt(x.cost)}` },
    { h: "Кліки", r: x => fmt(x.clicks) },
    { h: "Покази", r: x => fmt(x.impressions) },
    { h: "CTR", r: x => `${x.ctr}%` },
    { h: "CPC", r: x => `${sign}${fmt(x.cpc)}` },
    { h: "Конв.", r: x => fmt(x.conversions) },
    { h: "CPA", r: x => x.cpa ? `${sign}${fmt(x.cpa)}` : "—" },
    { h: "ROAS", r: x => x.roas ? `${x.roas}×` : "—" },
  ];
  return (
    <div style={{ ...card }}>
      {title && <div style={{ fontSize: 12, fontWeight: 600, color: t.textMuted, textTransform: "uppercase", letterSpacing: 1, marginBottom: 6 }}>{title}</div>}
      <div style={{ fontSize: 11, color: t.textMuted, marginBottom: 10 }}>Топ {rows.length} за витратами</div>
      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
          <thead>
            <tr style={{ color: t.textMuted, fontSize: 11, textTransform: "uppercase" }}>
              <th style={{ textAlign: "left", padding: "6px 8px" }}>{labelCol || "Назва"}</th>
              {cols.map(c => <th key={c.h} title={COL_DESC[c.h]} style={{ textAlign: "right", padding: "6px 8px", whiteSpace: "nowrap", cursor: COL_DESC[c.h] ? "help" : "default" }}>{c.h}</th>)}
            </tr>
          </thead>
          <tbody>
            {rows.map((x, i) => (
              <tr key={i} style={{ borderTop: `1px solid ${t.border}` }}>
                <td style={{ padding: "7px 8px" }}>
                  <div style={{ fontWeight: 500 }}>{x.name}</div>
                  {x.sub && <div style={{ fontSize: 11, color: t.textMuted }}>{x.sub}</div>}
                </td>
                {cols.map(c => <td key={c.h} style={{ padding: "7px 8px", textAlign: "right", whiteSpace: "nowrap" }}>{c.r(x)}</td>)}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function fmt(n) {
  const num = Number(n || 0);
  return num.toLocaleString("uk-UA", { maximumFractionDigits: 2 });
}
